import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus, Calculator, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Salary() {
  const [records, setRecords] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exporting, setExporting] = useState({});
  const [pdfLoading, setPdfLoading] = useState({});
  const [attendanceFetching, setAttendanceFetching] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [formData, setFormData] = useState({
    driver_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    base_salary: '',
    working_days: '',
    bonus: 0,
    deductions: 0,
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const total =
      parseFloat(formData.base_salary || 0) +
      parseFloat(formData.bonus || 0) -
      parseFloat(formData.deductions || 0);
    setCalculatedTotal(total);
  }, [formData.base_salary, formData.bonus, formData.deductions]);

  // Auto-fetch attendance when driver/month/year changes
  useEffect(() => {
    if (formData.driver_id && formData.month && formData.year) {
      fetchAttendanceSummary(formData.driver_id, formData.month, formData.year);
    } else {
      setAttendanceSummary(null);
    }
  }, [formData.driver_id, formData.month, formData.year]);

  const loadData = async () => {
    try {
      const [recordsRes, driversRes] = await Promise.all([api.get('/salary'), api.get('/drivers')]);
      setRecords(recordsRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceSummary = async (driverId, month, year) => {
    setAttendanceFetching(true);
    try {
      const res = await api.get(`/salary/attendance-summary?driver_id=${driverId}&month=${month}&year=${year}`);
      setAttendanceSummary(res.data);
      // Auto-fill working days
      if (res.data.present_days > 0) {
        setFormData(prev => ({ ...prev, working_days: res.data.present_days }));
      }
    } catch {
      setAttendanceSummary(null);
    } finally {
      setAttendanceFetching(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(prev => ({ ...prev, [format]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/salary/export-${format}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salary_report.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Salary ${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleDownloadPayslip = async (salaryId) => {
    setPdfLoading(prev => ({ ...prev, [salaryId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/salary/${salaryId}/payslip-pdf`, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${salaryId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch {
      toast.error('Failed to download payslip');
    } finally {
      setPdfLoading(prev => ({ ...prev, [salaryId]: false }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/salary', {
        ...formData,
        base_salary: parseFloat(formData.base_salary),
        working_days: parseInt(formData.working_days),
        bonus: parseFloat(formData.bonus || 0),
        deductions: parseFloat(formData.deductions || 0),
      });
      toast.success('Salary record created successfully');
      setDialogOpen(false);
      setFormData({
        driver_id: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        base_salary: '',
        working_days: '',
        bonus: 0,
        deductions: 0,
      });
      setAttendanceSummary(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create salary record');
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find((d) => d.driver_id === driverId);
    return driver ? driver.full_name : 'Unknown';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  return (
    <div data-testid="salary-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Salary Management</h1>
          <p className="text-gray-600">Manage driver salary records linked with attendance</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => handleExport('csv')}
            disabled={exporting.csv}
            data-testid="export-salary-csv"
            className="border-shizah-blue text-shizah-blue hover:bg-blue-50"
          >
            <Download size={18} className="mr-2" />
            {exporting.csv ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('xlsx')}
            disabled={exporting.xlsx}
            data-testid="export-salary-xlsx"
            className="border-green-600 text-green-700 hover:bg-green-50"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            {exporting.xlsx ? 'Exporting...' : 'Export XLS'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setAttendanceSummary(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-salary-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
                <Plus size={20} className="mr-2" />
                Add Salary Record
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="salary-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Salary Record</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Driver *</Label>
                  <Select
                    value={formData.driver_id}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, driver_id: value }))}
                    data-testid="select-driver"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select driver" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers
                        .filter((d) => d.status === 'active')
                        .map((driver) => (
                          <SelectItem key={driver.driver_id} value={driver.driver_id}>
                            {driver.driver_id} - {driver.full_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="month">Month *</Label>
                  <Select
                    value={formData.month.toString()}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, month: parseInt(value) }))}
                    data-testid="select-month"
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((month, index) => (
                        <SelectItem key={index + 1} value={(index + 1).toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="year">Year *</Label>
                  <Input
                    id="year"
                    type="number"
                    data-testid="input-year"
                    value={formData.year}
                    onChange={(e) => setFormData((prev) => ({ ...prev, year: parseInt(e.target.value) }))}
                    required
                  />
                </div>

                {/* Attendance Summary Card */}
                {formData.driver_id && (
                  <div className="col-span-2">
                    {attendanceFetching ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-blue-700 text-sm">
                        <Loader2 className="animate-spin" size={16} /> Fetching attendance data...
                      </div>
                    ) : attendanceSummary ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="attendance-summary-card">
                        <p className="text-sm font-semibold text-blue-900 mb-1">Attendance Summary ({monthNames[formData.month - 1]} {formData.year})</p>
                        <div className="flex gap-6 text-sm">
                          <span className="text-green-700">Present: <strong>{attendanceSummary.present_days}</strong></span>
                          <span className="text-red-700">Absent: <strong>{attendanceSummary.absent_days}</strong></span>
                          <span className="text-gray-600">Total Marked: <strong>{attendanceSummary.total_marked}</strong></span>
                        </div>
                        {attendanceSummary.present_days > 0 && (
                          <p className="text-xs text-blue-600 mt-1">Working days auto-filled from attendance</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                        No attendance data found for this driver/month. Enter working days manually.
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="base_salary">Base Salary (INR) *</Label>
                  <Input
                    id="base_salary"
                    type="number"
                    step="0.01"
                    data-testid="input-base-salary"
                    value={formData.base_salary}
                    onChange={(e) => setFormData((prev) => ({ ...prev, base_salary: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="working_days">Working Days *</Label>
                  <Input
                    id="working_days"
                    type="number"
                    data-testid="input-working-days"
                    value={formData.working_days}
                    onChange={(e) => setFormData((prev) => ({ ...prev, working_days: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="bonus">Bonus (INR)</Label>
                  <Input
                    id="bonus"
                    type="number"
                    step="0.01"
                    data-testid="input-bonus"
                    value={formData.bonus}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bonus: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="deductions">Deductions (INR)</Label>
                  <Input
                    id="deductions"
                    type="number"
                    step="0.01"
                    data-testid="input-deductions"
                    value={formData.deductions}
                    onChange={(e) => setFormData((prev) => ({ ...prev, deductions: e.target.value }))}
                  />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="calculated-total">
                <div className="flex items-center gap-2 text-green-800">
                  <Calculator size={20} />
                  <span className="font-semibold">Calculated Total Salary:</span>
                  <span className="text-xl font-bold">INR {calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-salary-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
                  Create Record
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="salary-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Driver</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Period</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Base Salary</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Working Days</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Bonus</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Deductions</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Total</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Payslip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No salary records found. Create your first record.
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.salary_id} className="hover:bg-gray-50" data-testid={`salary-row-${record.salary_id}`}>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      <div>{getDriverName(record.driver_id)}</div>
                      <div className="text-xs text-gray-500">{record.driver_id}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {monthNames[record.month - 1]} {record.year}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-mono">
                      INR {record.base_salary.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-right">{record.working_days}</td>
                    <td className="px-6 py-4 text-sm text-green-600 text-right font-mono">
                      +INR {record.bonus.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600 text-right font-mono">
                      -INR {record.deductions.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold font-mono">
                      INR {record.total_salary.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadPayslip(record.salary_id)}
                        disabled={pdfLoading[record.salary_id]}
                        data-testid={`payslip-${record.salary_id}`}
                        className="text-shizah-blue hover:bg-blue-50"
                      >
                        {pdfLoading[record.salary_id] ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
